import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { from, mergeMap, lastValueFrom, merge } from 'rxjs'
import OCRScopeWork from 'wunder/OCRScopeWork'
import OCRScopeWorkStore from 'wunder/OCRScopeWorkStore'
import { Id } from 'types/definitions'
import OCRScopingJobsPermissionViewModel from '../../OCRScopingJobsPermissionViewModel'
import { AlertState, AlertFactory } from '../../../../../alerts/Alert'
import JobStore from '../../../../JobStore'
import OCRConfirmBulkSubmitModalViewModel from '../Modals/OCRConfirmBulkSubmitModal/OCRConfirmBulkSubmitModalViewModel'
import OCRConfirmBulkReviewModalViewModel from '../Modals/OCRConfirmBulkReviewModal/OCRConfirmBulkReviewModalViewModel'
import OCRConfirmBulkRerunModalViewModel from '../Modals/OCRConfirmBulkRerunModal/OCRConfirmBulkRerunModalViewModel'
import {
  OCRScopeWorkStatusFilterType,
  OCRScopeWorkStatusFilterTypeId,
} from './OCRScopingJobSidePanelViewModel'
import InfiniteScrollProvider from '../../../../../utils/InfiniteScrollProvider'
import OCRBulkProgressModalViewModel from '../Modals/OCRBulkProgressModal/OCRBulkProgressModalViewModel'

type BulkActionError = {
  item: OCRScopeWork
  error: Error
}

export default class OCRScopingJobBulkActionViewModel {
  readonly confirmBulkSubmitModalVM = new OCRConfirmBulkSubmitModalViewModel(
    this
  )

  readonly confirmBulkReviewModalVM = new OCRConfirmBulkReviewModalViewModel(
    this
  )

  readonly confirmBulkRerunModalVM = new OCRConfirmBulkRerunModalViewModel(this)

  readonly bulkProgressModalVM = new OCRBulkProgressModalViewModel(this)

  @observable
  selectedStatusFilter?: OCRScopeWorkStatusFilterType = undefined

  @observable itemsSelectedForBulkAction = new Map<Id, OCRScopeWork>()
  @observable bulkActionSelectedCount = 0
  @observable bulkActionCompletedCount = 0
  @observable bulkActionErrors: BulkActionError[] = []
  @observable isProcessing = false
  @observable bulkProcessWasCancelled = false

  constructor(
    private ocrScopingJobsPermissionViewModel: OCRScopingJobsPermissionViewModel,
    private ocrScopeWorkStore: OCRScopeWorkStore,
    private jobStore: JobStore,
    private scrollProvider: InfiniteScrollProvider<OCRScopeWork>,
    private newAlert: AlertFactory
  ) {
    makeObservable(this)

    merge(
      this.confirmBulkSubmitModalVM.show$,
      this.confirmBulkReviewModalVM.show$,
      this.confirmBulkRerunModalVM.show$
    ).subscribe(() => {
      this.setBulkActionSelectedCount(this.itemsSelectedForBulkAction.size)
      this.setBulkActionCompletedCount(0)
    })
  }

  @computed
  get canViewScopingJobs() {
    return this.ocrScopingJobsPermissionViewModel.canViewScopingJobs
  }

  @computed
  get canManageOCRScopingJobs() {
    return this.ocrScopingJobsPermissionViewModel.canManageOCRScopingJobs
  }

  @computed
  get hasAnyItemsSelectedForBulkAction() {
    return this.itemsSelectedForBulkAction.size > 0
  }

  @computed
  get hasAllItemsSelectedForBulkAction() {
    return (
      this.itemsSelectedForBulkAction.size === this.scrollProvider.items.length
    )
  }

  @computed
  get hasSomeItemsSelectedForBulkAction() {
    return (
      this.itemsSelectedForBulkAction.size > 0 &&
      this.itemsSelectedForBulkAction.size < this.scrollProvider.items.length
    )
  }

  @action.bound
  toggleItemForBulkAction(item: OCRScopeWork) {
    if (this.itemsSelectedForBulkAction.has(item.id)) {
      this.itemsSelectedForBulkAction.delete(item.id)
      return
    }

    this.itemsSelectedForBulkAction.set(item.id, item)
  }

  @action.bound
  clearItemsSelectedForBulkAction() {
    this.itemsSelectedForBulkAction.clear()
  }

  @action.bound
  toggleSelectAllItemForBulkAction() {
    if (
      this.hasSomeItemsSelectedForBulkAction ||
      this.hasAllItemsSelectedForBulkAction
    ) {
      this.clearItemsSelectedForBulkAction()
      return
    }

    this.scrollProvider.items.forEach((item) => {
      this.itemsSelectedForBulkAction.set(item.id, item)
    })
  }

  @computed
  get hasBulkActionErrors() {
    return this.bulkActionErrors.length > 0
  }

  @computed
  get progress() {
    if (this.bulkActionSelectedCount === 0) {
      return 0
    }

    if (!this.bulkProcess.pending) {
      return 100
    }

    return Math.round(
      (this.bulkActionCompletedCount / this.bulkActionSelectedCount) * 100
    )
  }

  @computed
  get bulkActionCompletedSuccessfully() {
    return this.bulkActionCompletedCount - this.bulkActionErrors.length
  }

  @action.bound
  setBulkActionSelectedCount(count: number) {
    this.bulkActionSelectedCount = count
  }

  @action.bound
  setBulkActionCompletedCount(count: number) {
    this.bulkActionCompletedCount = count
  }

  @action.bound
  setIsProcessing(isProcessing: boolean) {
    this.isProcessing = isProcessing
  }

  @action.bound
  addBulkActionErrors(errors: BulkActionError) {
    this.bulkActionErrors.push(errors)
  }

  @action.bound
  clearBulkActionErrors() {
    this.bulkActionErrors = []
  }

  @action.bound
  activate(statusFilter?: OCRScopeWorkStatusFilterType) {
    this.selectedStatusFilter = statusFilter
    this.clearItemsSelectedForBulkAction()
    this.clearBulkActionErrors()
  }

  @action.bound
  setBulkProcessWasCancelled(bulkProcessWasCancelled: boolean) {
    this.bulkProcessWasCancelled = bulkProcessWasCancelled
  }

  bulkProcess = task.resolved(
    async (request: (item: OCRScopeWork) => Promise<any>) => {
      const items = Array.from(this.itemsSelectedForBulkAction.values())
      if (!items.length) {
        return
      }

      this.clearBulkActionErrors()
      this.setBulkActionCompletedCount(0)
      this.setIsProcessing(true)
      this.setBulkProcessWasCancelled(false)
      this.bulkProgressModalVM.show()

      await lastValueFrom(
        from(items).pipe(
          mergeMap(async (item) => {
            if (this.bulkProcessWasCancelled) {
              return
            }

            await request(item).catch((error) => {
              this.addBulkActionErrors({ item, error })
            })
            this.setBulkActionCompletedCount(this.bulkActionCompletedCount + 1)
          }, 5)
        )
      )
      this.setIsProcessing(false)
    }
  )

  bulkSubmit = task.resolved(async () => {
    if (!this.canViewScopingJobs) {
      return
    }

    await this.bulkProcess((item) =>
      this.jobStore.submitRequestV3(item.jobId, {
        paymentMethod: null,
        bypassBilling: false,
      })
    )

    if (this.bulkActionCompletedSuccessfully > 0) {
      this.newAlert()
        .saying(
          'Success!',
          `${this.bulkActionCompletedSuccessfully} jobs were submitted successfully.`
        )
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    }

    if (this.bulkActionErrors.length > 0) {
      this.newAlert()
        .saying(
          'Error!',
          `${this.bulkActionErrors.length} jobs failed submission.`
        )
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  bulkReview = task.resolved(async () => {
    if (
      !this.canManageOCRScopingJobs ||
      this.selectedStatusFilter?.id !==
        OCRScopeWorkStatusFilterTypeId.UnderReview
    ) {
      return
    }

    await this.bulkProcess((item) =>
      this.ocrScopeWorkStore.review(Id.assert(item.id))
    )

    if (this.bulkActionCompletedSuccessfully > 0) {
      this.newAlert()
        .saying(
          'Success!',
          `${this.bulkActionCompletedSuccessfully} scoping jobs were moved to ready to submit.`
        )
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    }

    if (this.bulkActionErrors.length > 0) {
      this.newAlert()
        .saying(
          'Error!',
          `${this.bulkActionErrors.length} scoping jobs failed while trying to set them as ready to submit.`
        )
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  bulkRerun = task.resolved(async () => {
    if (
      !this.canManageOCRScopingJobs ||
      this.selectedStatusFilter?.id !==
        OCRScopeWorkStatusFilterTypeId.ErrorOccurred
    ) {
      return
    }

    await this.bulkProcess((item) =>
      this.ocrScopeWorkStore.rerun(Id.assert(item.id))
    )

    if (this.bulkActionCompletedSuccessfully > 0) {
      this.newAlert()
        .saying(
          'Success!',
          `${this.bulkActionCompletedSuccessfully} scoping job were sent to rerun OCR.`
        )
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    }

    if (this.bulkActionErrors.length > 0) {
      this.newAlert()
        .saying(
          'Error!',
          `${this.bulkActionErrors.length} scoping jobs failed while trying to trigger the rerun.`
        )
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })
}
